/* Admin · Design Studio Dashboard
 * --------------------------------
 * Lists every studio_request with its base design, customer, and payment
 * status. Acts as the founder's revenue + funnel monitor for the new
 * Studio module.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, Sparkles, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, tone = "navy", testId }) => {
  const tones = {
    navy: "bg-navy text-white border-navy-600",
    gold: "bg-gold text-navy border-gold-dark",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    white: "bg-white text-navy border-border",
  };
  return (
    <div className={`rounded-2xl border-2 px-3.5 py-3 ${tones[tone]} shadow-sm`} data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-extrabold tabular-nums">{value}</div>
    </div>
  );
};

export default function StudioRequestsPanel({ onToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | paid | pending

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: payload } = await axios.get(`${API}/admin/studio/requests`);
      setData(payload);
    } catch {
      onToast?.("Failed to load studio requests", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.items;
    return data.items.filter((x) => x.payment_status === filter);
  }, [data, filter]);

  if (loading) {
    return (
      <section className="mt-6 rounded-3xl bg-white border-2 border-navy/10 p-5 text-center text-muted-foreground" data-testid="studio-admin-loading">
        Loading Design Studio dashboard…
      </section>
    );
  }
  if (!data) return null;
  const s = data.summary;

  return (
    <section className="mt-6 rounded-3xl bg-white border-2 border-navy/10 p-5 sm:p-6 shadow-sm" data-testid="studio-admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold inline-flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Premium · New
          </div>
          <h2 className="mt-1 text-xl sm:text-2xl font-extrabold text-navy">Design Studio · Requests & Revenue</h2>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          data-testid="studio-admin-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-navy text-white text-xs font-bold hover:bg-navy-600 disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Stat testId="studio-stat-total" label="Total" value={s.total} tone="navy" />
        <Stat testId="studio-stat-paid" label="Paid" value={s.paid} tone="green" />
        <Stat testId="studio-stat-pending" label="Pending" value={s.pending} tone="white" />
        <Stat testId="studio-stat-revenue" label="Revenue" value={`₹${s.revenue_inr.toLocaleString("en-IN")}`} tone="gold" />
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {[
          { id: "all", label: `All (${s.total})` },
          { id: "paid", label: `Paid (${s.paid})` },
          { id: "pending", label: `Pending (${s.pending})` },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`studio-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
              filter === f.id ? "bg-navy text-white border-navy" : "bg-white text-navy border-border hover:border-navy/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-3 overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm" data-testid="studio-admin-table">
          <thead className="bg-sand-alt text-navy text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-bold">Receipt</th>
              <th className="text-left px-3 py-2 font-bold">Customer</th>
              <th className="text-left px-3 py-2 font-bold">Plot · BHK</th>
              <th className="text-left px-3 py-2 font-bold">Base design</th>
              <th className="text-right px-3 py-2 font-bold">Amount</th>
              <th className="text-center px-3 py-2 font-bold">Status</th>
              <th className="text-left px-3 py-2 font-bold">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="7" className="px-3 py-6 text-center text-muted-foreground">No requests yet.</td></tr>
            ) : (
              items.map((it) => {
                const req = it.requirements || {};
                const created = it.created_at ? new Date(it.created_at).toLocaleString("en-IN") : "";
                return (
                  <tr key={it.id} data-testid={`studio-row-${it.id}`} className="border-t border-border/60 hover:bg-sand-alt/40">
                    <td className="px-3 py-2 font-mono text-[12px] text-navy/80">{it.receipt_no}</td>
                    <td className="px-3 py-2">
                      <div className="font-bold text-navy">{it.customer_name}</div>
                      <div className="text-[11px] text-muted-foreground tabular-nums">{it.customer_whatsapp}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {req.plot_width}' × {req.plot_depth}' · {req.bhk}BHK · {req.floors}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-bold text-navy">{it.base_design_name}</div>
                      <code className="text-[11px] text-muted-foreground">{it.base_design_id}</code>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-navy">
                      ₹{Number(it.amount || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${
                        it.payment_status === "paid"
                          ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                          : it.payment_status === "failed"
                          ? "bg-red-50 border border-red-200 text-red-700"
                          : "bg-amber-50 border border-amber-200 text-amber-700"
                      }`}>
                        {it.payment_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">
                      {created}
                      {it.payment_status === "paid" && (
                        <a
                          href={it.pdf_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`studio-pdf-${it.id}`}
                          className="ml-2 inline-flex items-center gap-0.5 text-navy hover:text-gold-dark"
                        >
                          PDF <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
